import { API_BETA, AI_COMPANION_API, jsonHeadersWithToken, authHeaders, parseJsonSafe, getErrorMessage, getApiToken } from '@/lib/constants'
import { useSelectionStore } from '@/state/selection.store'
import { safeLocalStorage, safeSessionStorage } from '@/lib/safe-storage'

const BASE_URL = API_BETA

const getSearchParam = (key) => {
  return typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get(key) : null
}

const resolveUniqueUrl = (currentProject, projects) => {
  const projectIdFromUrl = getSearchParam('project')

  // Priority 0: Explicit uniqueUrl in URL (shared projects pass this directly)
  const uniqueUrlFromParam = getSearchParam('uniqueUrl')
  console.log('[resolveUniqueUrl] projectIdFromUrl:', projectIdFromUrl, 'uniqueUrlFromParam:', uniqueUrlFromParam, 'currentProject:', currentProject?.id, currentProject?.uniqueUrl, 'projects count:', projects?.length)
  if (uniqueUrlFromParam) {
    console.log('[resolveUniqueUrl] → Priority 0: using uniqueUrl from URL param:', uniqueUrlFromParam)
    return uniqueUrlFromParam
  }

  // Priority 1: Find project by URL parameter (most accurate for current view)
  if (projectIdFromUrl && projects?.length) {
    const fromUrlParam = projects.find(p => (p.id || p._id) === projectIdFromUrl)
    if (fromUrlParam?.uniqueUrl) {
      console.log('[resolveUniqueUrl] → Priority 1: found in projects list:', fromUrlParam.uniqueUrl)
      return fromUrlParam.uniqueUrl
    }
  }

  // Priority 2: Use cached currentProject only if it matches the URL param
  if (currentProject?.uniqueUrl) {
    const currentId = currentProject.id || currentProject._id
    if (!projectIdFromUrl || currentId === projectIdFromUrl) {
      console.log('[resolveUniqueUrl] → Priority 2: using currentProject:', currentProject.uniqueUrl)
      return currentProject.uniqueUrl
    }
  }

  console.log('[resolveUniqueUrl] → returning null, no uniqueUrl found')
  return null
}

export const getUniqueUrl = () => {
  const currentProject = useSelectionStore(state => state.currentProject)
  const projects = useSelectionStore(state => state.projects)
  return resolveUniqueUrl(currentProject, projects)
}

export const getUserId = () => {
  const { currentProject } = useSelectionStore.getState()
  if (currentProject?.userOrGuestId) return currentProject.userOrGuestId
  const fromUrl = getSearchParam('user')
  if (fromUrl) return fromUrl
  try {
    return JSON.parse(safeLocalStorage.getItem('user_data') || '{}').id || null
  } catch {
    return null
  }
}

export const getWebBuilderDetails = async (project = null, page = null, type = null) => {
  const params = new URLSearchParams()
  if (project) params.append('project', project)
  if (page) params.append('page', page)
  if (type) params.append('type', type)
  params.append('user', getUserId())

  const url = `${BASE_URL}guest/web-builder?${params.toString()}`

  const response = await fetch(url, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response)
    const message = getErrorMessage(errorData, 'Failed to fetch project details')
    throw new Error(message)
  }

  const result = await parseJsonSafe(response)
  return result.data || result
};

export const addNewPage = async (newPage, project = null) => {
  const params = new URLSearchParams()
  params.append('user', getUserId())
  const response = await fetch(`${BASE_URL}guest/web-builder/add-page?${params.toString()}`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ newBlueprint: newPage, project: project }),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response)
    const message = getErrorMessage(errorData, 'Failed to create page')
    throw new Error(message)
  }

  const result = await parseJsonSafe(response)
  return result.data || result
};

export const deletePage = async (page, project = null) => {
  const params = new URLSearchParams()
  params.append('user', getUserId())
  const response = await fetch(`${BASE_URL}guest/web-builder/delete-page?${params.toString()}`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ page, project: project }),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response)

    // Bypass the "webBuilderPublished is not defined" error
    if (errorData?.errorMessage?.includes('webBuilderPublished is not defined') ||
      errorData?.message?.includes('webBuilderPublished is not defined')) {
      // Treat as success
      return { success: true }
    }
    throw new Error(getErrorMessage(errorData, 'Failed to delete page'))
  }

  const result = await parseJsonSafe(response)
  return result.data || result
};

export const duplicatePage = async (page, project = null) => {
  const params = new URLSearchParams()
  params.append('pageName', page)
  params.append('project', project)
  params.append('user', getUserId())

  const url = `${BASE_URL}guest/web-builder/duplicate-page?${params.toString()}`

  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response)
    const message = getErrorMessage(errorData, 'Failed to duplicate page')
    throw new Error(message)
  }

  const result = await parseJsonSafe(response)
  return result.data || result
};

export const updatePageSettings = async (project = null, blueprint, context, options = {}) => {
  const params = new URLSearchParams()
  params.append('project', project)
  params.append('user', getUserId())

  const payload = {
    data: blueprint,
    project: project,
    rateLimit: 'true',
    responseEmpty: 'true',
    context,
    options,
    deviceId: safeSessionStorage.getItem('deviceId') || '',
  };

  const response = await fetch(`${BASE_URL}guest/web-builder?${params.toString()}`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response);
    throw new Error(getErrorMessage(errorData, 'Failed to update page settings'));
  }

  const result = await parseJsonSafe(response);
  return result.data || result;
};

export const uploadOgImage = async (file, projectId, pageName) => {
  if (file.size / 1024 / 1024 > 10) {
    throw new Error("File can't be larger than 10MB");
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', projectId);
  formData.append('metadata', JSON.stringify({ pageSettings: true, page: pageName }));

  const headers = authHeaders();

  const response = await fetch(`${BASE_URL}guest/supabase-assets-v2/upload-file`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response);
    throw new Error(getErrorMessage(errorData, 'Failed to upload image'));
  }

  const result = await parseJsonSafe(response);
  return result.data || result;
};

export const generateAIForPage = async (project = null, pageName, prompt, language = '') => {
  const params = new URLSearchParams();
  params.append('project', project);
  params.append('pageName', pageName);
  params.append('prompt', prompt);
  params.append('user', getUserId());
  if (language) {
    params.append('language', language);
  }

  const url = `${BASE_URL}guest/web-builder/generate-ai-for-page?${params.toString()}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  });

  if (!response.ok) {
    const errorData = await parseJsonSafe(response);
    const message = getErrorMessage(errorData, 'Failed to generate page with AI');
    throw new Error(message);
  }

  const result = await parseJsonSafe(response);
  return result.data || result;
};

